import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { from, Observable, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import { FormConfigDetails, FormConfigResponse, FormSubmissionResponse, SubmitFormPayload } from '../models/mercury-form';
import { FulfillmentUtils } from '../util/fulfillment-utils';

@Injectable({ providedIn: 'root' })
export class MercuryFormService {
  private readonly _http = inject(HttpClient);
  // In-memory handoff between the service-request page (which fetches the
  // full form list for an offering) and the mercury-form page (which needs
  // one entry from that list). Keeps mercury-form off the network — one
  // getFormConfigDetails call per user journey instead of two.
  private _formConfigs: FormConfigDetails[] | null = null;

  /**
   * Secure proxy endpoint (API Gateway → Lambda → SSM). The Lambda injects
   * `client_id`/`client_secret` and `X-User` (from the validated JWT)
   * server-side, so the browser only sends `Content-Type`. When the upstream
   * Mulesoft flow stalls, the Lambda enforces its own 25 s timeout and
   * returns HTTP 504 with `{ error: "upstream_timeout", path, timeoutMs }` —
   * that is the signal callers observe, so no client-side timeout is needed.
   */
  private readonly _apiUrl = environment.aws.fulfillmentEndpoint;

  private get _headers(): HttpHeaders {
    return new HttpHeaders({ 'Content-Type': 'application/json' });
  }

  fetchForm(formName: string): Observable<string> {
    return this._http.get(
      `${this._apiUrl}/forms/${formName}`,
      {
        headers: this._headers,
        responseType: 'text'
      }
    );
  }


  fetchOptions(formName: string, versionNo: string, payload: unknown): Observable<Object> {
    return this._postSigned<Object>(
      `${this._apiUrl}/forms/${formName}/version/${versionNo}/options`,
      payload
    );
  }

  getFormConfigDetails(offeringId: string): Observable<FormConfigResponse> {
    let params = new HttpParams();
    if (offeringId) {
      params = params.set('offeringId', offeringId);
    }
    return this._http.get<FormConfigResponse>(
      `${this._apiUrl}/formConfigDetails`,
      {
        headers: this._headers,
        params,
      }
    );
  }

  submitForm(payload: SubmitFormPayload): Observable<FormSubmissionResponse> {
    return this._postSigned<FormSubmissionResponse>(
      `${this._apiUrl}/offeringFulfillmentRequest`,
      payload
    );
  }

  /**
   * POST carrying the body's SHA-256 in `X-Amz-Content-Sha256`.
   *
   * Once fulfillment sits behind a Lambda Function URL, CloudFront's Origin
   * Access Control SigV4-signs the request and Lambda rejects a POST whose body
   * hash was never folded into that signature. The header is inert on the
   * current API Gateway path, which simply ignores it — that is what lets this
   * ship before the origin is repointed, instead of the SPA and the
   * infrastructure having to land in the same breath.
   *
   * Serialising here and posting the string matters: handing HttpClient an
   * object would let it re-serialise, and any difference in the bytes would
   * invalidate the hash we just computed.
   */
  private _postSigned<T>(url: string, payload: unknown): Observable<T> {
    const bodyText = JSON.stringify(payload);
    return from(this._sha256Hex(bodyText)).pipe(
      switchMap((bodyHash) =>
        this._http.post<T>(url, bodyText, {
          headers: this._headers.set('X-Amz-Content-Sha256', bodyHash),
        })
      )
    );
  }

  /** Hex-encoded SHA-256 of a UTF-8 string, via the Web Crypto API. */
  private async _sha256Hex(text: string): Promise<string> {
    const digest = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(text)
    );
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  setFormConfigs(list: FormConfigDetails[]): void {
    this._formConfigs = Array.isArray(list) ? list : null;
  }

  getFormConfigByFormId(formId: string, offeringId?: string | null): FormConfigDetails | undefined {
    return FulfillmentUtils.findFormConfig(this._formConfigs, formId, offeringId);
  }

  clear(): void {
    this._formConfigs = null;
  }
}
